import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { LockOutlined, ClockCircleOutlined, SafetyOutlined, CheckOutlined } from '@ant-design/icons';
import { nodeKindMeta } from '@/theme/tokens';
import type { FlowNode as IRNode } from '@/flow-ir/types';
import type { OlNode } from '@/flow-ir/toReactFlow';
import { StatusPill } from '@/components/StatusPill';
import { resolveGlyph } from '../glyphs';
import { configChipValue } from './configChip';
import type { DiagOverlay } from '../diagOverlay';

/** Obligation chips shown before collapsing into a "+N" count (density: scannable at zoom-out). */
const MAX_OBLIGATION_CHIPS = 2;

function Chip({
  children,
  variant,
  title,
}: {
  children: React.ReactNode;
  variant?: 'obligation' | 'entity' | 'governance';
  title?: string;
}) {
  const cls = variant ? `ol-chip ol-chip--${variant}` : 'ol-chip';
  return (
    <span className={cls} title={title}>
      {children}
    </span>
  );
}

/** Kind-specific detail chips. Read-only projection of the IR fields that matter at a glance. */
function KindDetail({ node }: { node: IRNode }) {
  switch (node.kind) {
    case 'capability': {
      const entries = Object.entries(node.config ?? {}).slice(0, 2);
      return (
        <>
          {entries.map(([k, v]) => (
            <Chip key={k} title={`config.${k}`}>
              {k}: {configChipValue(v)}
            </Chip>
          ))}
          {node.idempotency && <Chip title="idempotency">{node.idempotency.mode.toLowerCase()}</Chip>}
        </>
      );
    }
    case 'decision':
      return (
        <>
          {(node.outcomes ?? []).map((o) => (
            <StatusPill key={o} status={o} size="xs" />
          ))}
        </>
      );
    case 'humanTask':
    case 'humanReviewTask':
      return (
        <>
          {node.reviewerPool && <Chip title="reviewer pool">{node.reviewerPool}</Chip>}
          {node.sla && (
            <Chip title="SLA">
              <ClockCircleOutlined /> {node.sla}
            </Chip>
          )}
          {node.makerChecker && <Chip title="maker-checker">4-eyes</Chip>}
        </>
      );
    case 'subFlow':
      return (
        <>
          {node.flow && (
            <Chip title="referenced flow">
              {node.flow.id}@{node.flow.version}
            </Chip>
          )}
        </>
      );
    case 'dynamicBranch':
      return (
        <>
          {node.flow?.id && (
            <Chip title="flow run per item">
              {node.flow.id}@{node.flow.version}
            </Chip>
          )}
          {typeof node.guardrails?.maxFanOut === 'number' && (
            <Chip title="maximum children">up to {node.guardrails.maxFanOut}</Chip>
          )}
          {node.successOutcomes?.length ? (
            <Chip title="child outcomes that count as success">counts {node.successOutcomes.join(', ')}</Chip>
          ) : null}
        </>
      );
    case 'multiSourceScoring':
      return (
        <>
          <Chip title="sources">{node.sources?.length ?? 0} sources</Chip>
          {typeof node.threshold === 'number' && <Chip title="threshold">≥ {node.threshold}</Chip>}
        </>
      );
    case 'terminal':
      return node.outcome ? <StatusPill status={node.outcome} size="xs" /> : null;
    case 'join':
    case 'parallel':
      return (
        <>
          {node.policy && <Chip title="join policy">{node.policy}</Chip>}
          {node.quorum != null && <Chip title="quorum">q={node.quorum}</Chip>}
        </>
      );
    case 'llmTask':
      return (
        <>
          {node.pathType && (
            <Chip
              variant="governance"
              title="AI fallback direction (ADR-006)"
            >
              <SafetyOutlined /> {node.pathType.toLowerCase()}
            </Chip>
          )}
          {node.params?.citeOrShutUp && <Chip title="output validator">cite-or-shut-up</Chip>}
          {node.fallback && <Chip title="fallback">{node.fallback.mode.toLowerCase()}</Chip>}
        </>
      );
    case 'confidenceGate':
      return (
        <>
          {node.source && <Chip title="gates the confidence of">← {node.source}</Chip>}
          {typeof node.threshold === 'number' && <Chip title="threshold">≥ {node.threshold}</Chip>}
        </>
      );
    default:
      return null;
  }
}

function FlowNodeInner({ data, selected, isConnectable }: NodeProps<OlNode>) {
  const node = data.node;
  const meta = nodeKindMeta(node.kind);
  // Overlay fields ride on the node data as extras (OlNodeData has an open index signature).
  const overlay: DiagOverlay = {
    diagTitle: data.diagTitle as string | undefined,
    diagStale: data.diagStale as boolean | undefined,
    validated: data.validated as boolean | undefined,
  };

  const classes = ['ol-node'];
  if (selected) classes.push('selected');
  if (meta.ai) classes.push('ol-node--ai');
  if (meta.reserved) classes.push('ol-node--reserved');
  if (node.kind === 'terminal') classes.push('ol-node--terminal');
  if (node.kind === 'decision') classes.push('ol-node--decision');
  if (isConnectable) classes.push('ol-node--editable');
  if (data.diag) classes.push(`diag-${data.diag}`);
  if (data.diag && overlay.diagStale) classes.push('diag-stale');
  if (data.diffState === 'added') classes.push('diff-added');
  if (data.diffState === 'changed') classes.push('diff-changed');

  const configurableCount = node.configurable ? Object.keys(node.configurable).length : 0;
  const diagSymbol = data.diag === 'error' ? '●' : data.diag === 'warning' ? '▲' : 'ℹ';
  const diagAria = data.diag
    ? ` — ${data.diagCount ?? 1} ${data.diag}${(data.diagCount ?? 1) > 1 ? 's' : ''}${overlay.diagStale ? ' (from last validation)' : ''}`
    : overlay.validated
      ? ' — validated, no findings'
      : '';

  return (
    <div
      className={classes.join(' ')}
      style={{ ['--ol-node-accent' as string]: meta.accent }}
      role="group"
      aria-label={`${meta.label}: ${node.name ?? node.id}${diagAria}`}
    >
      {data.diag && (
        <span
          className="ol-node__diag"
          title={overlay.diagTitle ?? `${data.diagCount ?? 1} ${data.diag} diagnostic(s)`}
        >
          {diagSymbol}
          {(data.diagCount ?? 1) > 1 ? data.diagCount : ''}
        </span>
      )}
      {data.diffState === 'added' && (
        <span className="ol-node__difftag" title="Added vs the compared version">
          + added
        </span>
      )}
      {data.diffState === 'changed' && (
        <span className="ol-node__difftag" title="Changed vs the compared version">
          ~ changed
        </span>
      )}
      {/* Control-flow handles: the vertical spine (top in, bottom out). */}
      <Handle type="target" position={Position.Top} id="in" isConnectable={isConnectable} />
      <Handle type="source" position={Position.Bottom} id="out" isConnectable={isConnectable} />
      {/* Data-layer handles: side anchors, never on the control spine. */}
      <Handle
        type="target"
        position={Position.Left}
        id="data-l"
        className="ol-handle--data"
        isConnectable={isConnectable}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="data-r"
        className="ol-handle--data"
        isConnectable={isConnectable}
      />

      <div className="ol-node__head">
        <span className="ol-node__kind">
          {resolveGlyph(meta.glyph)}
          {meta.label}
          {meta.reserved && ' · reserved'}
        </span>
        {overlay.validated && !data.diag && (
          <span className="ol-node__validated" title="No findings on the last validation" aria-hidden>
            <CheckOutlined />
          </span>
        )}
        {node.locked && (
          <LockOutlined
            style={{
              marginInlineStart: overlay.validated && !data.diag ? 4 : 'auto',
              color: 'var(--ol-text-tertiary)',
              fontSize: 11,
            }}
            title="Locked — variant children cannot override"
          />
        )}
      </div>

      <div className="ol-node__name">{node.name ?? node.id}</div>
      {node.capability && <div className="ol-node__cap">{node.capability}</div>}

      <div className="ol-node__chips">
        {node.entityBinding && (
          <Chip variant="entity" title="entity role binding">
            ⟶ {node.entityBinding}
          </Chip>
        )}
        <KindDetail node={node} />
        {(node.obligations ?? []).slice(0, MAX_OBLIGATION_CHIPS).map((o) => (
          <Chip key={o} variant="obligation" title="regulatory obligation">
            {o}
          </Chip>
        ))}
        {(node.obligations ?? []).length > MAX_OBLIGATION_CHIPS && (
          <Chip
            variant="obligation"
            title={`regulatory obligations: ${(node.obligations ?? []).slice(MAX_OBLIGATION_CHIPS).join(', ')}`}
          >
            +{(node.obligations ?? []).length - MAX_OBLIGATION_CHIPS}
          </Chip>
        )}
        {configurableCount > 0 && (
          <Chip variant="governance" title="@configurable fields">
            {configurableCount} cfg
          </Chip>
        )}
        {node.skippable && (
          <Chip variant="governance" title="optional step (SKIPPED excluded from roll-up)">
            skippable
          </Chip>
        )}
      </div>
    </div>
  );
}

export const FlowNode = memo(FlowNodeInner);
